import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import PaymentModal from "./PaymentModal";
import CartDisplay from "./CartDisplay";
import QuickButtons from "./QuickButtons";
import DrawerCount from "./DrawerCount";
import LoyaltyLookup from "./LoyaltyLookup";
import DiscountModal from "./DiscountModal";
import PinSwitch from "./PinSwitch";
import ClockPunch from "./ClockPunch";
import { kickDrawer } from "../../services/printing";

interface SaleLine {
  id: string;
  product_id: string;
  sellable_format_id: string;
  qty: number;
  oz_total: number;
  cash_price_at_sale: number;
  card_price_at_sale: number;
  discount_applied: number;
  discount_type: string;
  discount_value: number;
  discount_reason: string;
  product_name: string;
  format_name: string;
  package_type: string;
}

interface Sale {
  id: string;
  status: string;
  subtotal_cash: number;
  subtotal_card: number;
  tax_cash: number;
  tax_card: number;
  total_cash: number;
  total_card: number;
  cart_discount_type: string;
  cart_discount_value: number;
  cart_discount_amount: number;
  cart_discount_reason: string;
  total_discount: number;
  lines: SaleLine[] | null;
}

interface SearchResult {
  id: string;
  product_id: string;
  product_name: string;
  name: string;
  format_name?: string;
  package_type: string;
  cash_price: number;
  card_price: number;
  oz_per_unit: number;
  upc: string;
}

interface FormatChoice {
  id: string;
  product_id: string;
  product_name: string;
  name: string;
  package_type: string;
  cash_price: number;
  card_price: number;
  oz_per_unit: number;
}

interface UpgradeOffer {
  product_id: string;
  current_lines: Array<{ line_id: string; format_name: string; qty: number; cash_price: number }>;
  current_total: number;
  upgrade_format_id: string;
  upgrade_format_name: string;
  upgrade_package_type: string;
  upgrade_qty: number;
  upgrade_total: number;
  savings: number;
  leftover_lines: Array<{ format_name: string; qty: number }>;
}

export default function Register() {
  const [sale, setSale] = useState<Sale | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formatChoices, setFormatChoices] = useState<FormatChoice[] | null>(null);
  const [upgradeOffers, setUpgradeOffers] = useState<UpgradeOffer[]>([]);
  const [showDrawerCount, setShowDrawerCount] = useState<"open" | "close" | null>(null);
  const [drawerSession, setDrawerSession] = useState<string | null>(null);
  const [drawerOpeningBalance, setDrawerOpeningBalance] = useState(0);
  const [drawerCashSales, setDrawerCashSales] = useState(0);
  const [drawerCashRefunds, setDrawerCashRefunds] = useState(0);
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [loyaltyCustomer, setLoyaltyCustomer] = useState<{
    id: string; phone: string; first_name: string; last_name: string;
    points_balance: number; lifetime_spend: number; last_visit: string | null;
  } | null>(null);
  const [loyaltySettings, setLoyaltySettings] = useState({ redemption_threshold: 100, redemption_value: 5 });
  const [toastMsg, setToastMsg] = useState<{ text: string; type: "error" | "success" } | null>(null);
  const [showCartDiscount, setShowCartDiscount] = useState(false);
  const [showHoldInput, setShowHoldInput] = useState(false);
  const [holdLabel, setHoldLabel] = useState("");
  const [showHeldSales, setShowHeldSales] = useState(false);
  const [heldSales, setHeldSales] = useState<Array<{
    id: string; hold_label: string; held_at: string; total_cash: number;
    total_card: number; item_count: number; held_by_name: string;
  }>>([]);
  const [trainingMode, setTrainingMode] = useState(false);
  // Paula 11/1: a barcode Karen does not know is learned at the register. The
  // scan that found nothing is remembered; when the cashier then finds the item
  // by name, Karen offers to save that barcode on the pack they picked.
  const [unknownUpc, setUnknownUpc] = useState<string | null>(null);
  const [upcOffer, setUpcOffer] = useState<{ upc: string; formatId: string; label: string } | null>(null);
  const [savingUpc, setSavingUpc] = useState(false);
  const [showPinSwitch, setShowPinSwitch] = useState(false);
  const [showClockPunch, setShowClockPunch] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user, logout, switchByPin } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const handlePinSwitch = async (pin: string) => {
    await switchByPin(pin, async (nextUserId) => {
      // Same person: keep the sale where it is.
      if (nextUserId === user?.id) return;
      // Hold an open sale with items for the outgoing cashier (auto-hold on switch)
      if (sale && sale.lines && sale.lines.length > 0) {
        try {
          await api.post(`/sales/${sale.id}/hold`, { label: `Auto-hold: ${user?.first_name} switched out` });
        } catch {
          // If hold fails (e.g. empty sale), just clear it
        }
      }
      setSale(null);
    });
    setShowPinSwitch(false);
  };

  const showToast = (text: string, type: "error" | "success" = "error") => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  const refreshSale = useCallback(async (saleId: string) => {
    const response = await api.get(`/sales/${saleId}`);
    setSale(response.data);
    // Check for upgrade opportunities
    try {
      const upgradeRes = await api.get(`/sales/${saleId}/upgrades`);
      setUpgradeOffers(upgradeRes.data || []);
    } catch { setUpgradeOffers([]); }
  }, []);

  const ensureOpenSale = useCallback(async (): Promise<string> => {
    if (sale && sale.status === "open") return sale.id;
    const openResponse = await api.get(`/sales/open?register_id=register-1&training=${trainingMode}`);
    if (openResponse.data) {
      setSale(openResponse.data);
      return openResponse.data.id;
    }
    const newResponse = await api.post("/sales", { register_id: "register-1", is_training: trainingMode });
    setSale(newResponse.data);
    return newResponse.data.id;
  }, [sale, trainingMode]);

  // The open drawer lives on the server (Paula 10/29): screen memory alone
  // forgot it on every refresh, and the closing count never saw the day's cash.
  const loadDrawer = useCallback(async () => {
    try {
      const r = await api.get("/sales/drawer/current", { params: { register_id: "register-1" } });
      const d = r.data;
      setDrawerSession(d ? d.id : null);
      setDrawerOpeningBalance(d ? Number(d.opening_balance) : 0);
      setDrawerCashSales(d ? Number(d.cash_sales_total) : 0);
      setDrawerCashRefunds(d ? Number(d.cash_refunds_total) : 0);
    } catch { /* keep what we have; the count screen still works */ }
  }, []);

  useEffect(() => { loadDrawer(); }, [loadDrawer]);

  useEffect(() => {
    // Restore open sale when Register mounts (e.g. returning from back office)
    if (!sale) {
      api.get(`/sales/open?register_id=register-1&training=${trainingMode}`)
        .then((r) => { if (r.data) setSale(r.data); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scanInputRef.current?.focus();
  }, [sale, showPayment]);

  useEffect(() => {
    api.get("/loyalty/settings").then((r) => {
      if (r.data) setLoyaltySettings({
        redemption_threshold: Number(r.data.redemption_threshold),
        redemption_value: Number(r.data.redemption_value),
      });
    }).catch(() => {});
  }, []);

  // Redeeming now takes money off the open sale, so the cart has to be refreshed
  // and any refusal has to reach the cashier. It used to fail silently, which
  // looked like the points had been spent for nothing.
  const handleRedeemPoints = async (customerId: string) => {
    if (!sale?.id) {
      setToastMsg({ text: "Ring the items first, then redeem points", type: "error" });
      return;
    }
    try {
      const res = await api.post("/loyalty/redeem", { customer_id: customerId, sale_id: sale.id });
      const reward = Number(res.data?.reward_value ?? 0);
      await refreshSale(sale.id);
      const cust = await api.get(`/loyalty/customers/${customerId}`);
      setLoyaltyCustomer(cust.data);
      setToastMsg({ text: `Took $${reward.toFixed(2)} off for ${res.data?.points_redeemed ?? ""} points`, type: "success" });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || "Could not redeem those points";
      setToastMsg({ text: msg, type: "error" });
    }
  };

  const addFormatToSale = useCallback(async (item: { product_id?: string; id?: string; sellable_format_id?: string }) => {
    const saleId = await ensureOpenSale();
    await api.post(`/sales/${saleId}/lines`, {
      product_id: item.product_id || item.id,
      sellable_format_id: item.sellable_format_id || item.id,
      qty: 1,
    });
    await refreshSale(saleId);
  }, [ensureOpenSale, refreshSale]);

  const handleScan = useCallback(async (upc: string) => {
    if (!upc.trim()) return;
    // Cancel the debounced search this scan's own keystrokes queued up.
    // A scanner types the barcode then sends Enter, so handleScan fires while a
    // 300ms handleSearch is still pending. That pending search then ran against
    // the same barcode and either auto-added the item a SECOND time or dropped a
    // result list under the search bar that had to be dismissed by picking from
    // it - which added it again. Paula 9/14, from the register: "you get the
    // product twice".
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchResults([]);
    setLoading(true);
    try {
      const lookupResponse = await api.get(`/products/lookup/${upc.trim()}`);
      const data = lookupResponse.data;

      if (data.multiple && data.formats) {
        const formats = data.formats as Array<Record<string, unknown>>;
        const productIds = new Set(formats.map((f) => String(f.product_id)));

        // Paula 10/6: when one barcode fits several sizes of the same product (the can
        // code on the single, the 6-pack and the case) the cashier chooses. The
        // 9/14 shortcut that rang the smallest size automatically is off: a 6-pack
        // scan was ringing as one can.
        // Several sizes or several products on one barcode: the cashier has to say which.
        setFormatChoices(formats.map((f: Record<string, unknown>) => ({
          id: String(f.id || f.sellable_format_id),
          product_id: String(f.product_id),
          product_name: String(f.product_name ?? ""),
          name: String(f.name || f.format_name || ""),
          package_type: String(f.package_type ?? ""),
          cash_price: Number(f.cash_price),
          card_price: Number(f.card_price),
          oz_per_unit: Number(f.oz_per_unit),
        })));
        setLoading(false);
        setSearchQuery("");
        return;
      }

      await addFormatToSale(data);
      setUnknownUpc(null);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const code = upc.trim().replace(/\D/g, "");
      if (status === 404 && /^\d+$/.test(upc.trim()) && code.length >= 6 && code.length <= 14) {
        setUnknownUpc(code);
        setUpcOffer(null);
        showToast("Barcode not in Karen yet. Search the item by name and tap it: Karen will offer to save the barcode.");
      } else {
        showToast("Product not found. Check the barcode or search by name.");
      }
    } finally {
      setLoading(false);
      setSearchQuery("");
    }
  }, [ensureOpenSale, refreshSale, addFormatToSale]);

  const saveUpcOffer = async () => {
    if (!upcOffer) return;
    setSavingUpc(true);
    try {
      const r = await api.post("/readiness/upc", { sellable_format_id: upcOffer.formatId, upc: upcOffer.upc });
      const shared = (r.data?.shared_with || []) as Array<{ product_name: string; format_name: string }>;
      showToast(shared.length
        ? `Barcode saved. It is also on ${shared[0].product_name} (${shared[0].format_name}), so the register will ask which.`
        : "Barcode saved. It will scan next time.", "success");
      setUpcOffer(null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      showToast(e.response?.data?.error || "Could not save the barcode.");
    } finally {
      setSavingUpc(false);
      scanInputRef.current?.focus();
    }
  };

  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const response = await api.get(`/products?search=${encodeURIComponent(query)}`);
      const results: SearchResult[] = [];
      for (const product of response.data) {
        if (product.sellable_formats) {
          const formats = typeof product.sellable_formats === "string"
            ? JSON.parse(product.sellable_formats) : product.sellable_formats;
          for (const fmt of formats) {
            if (!fmt.is_active) continue;
            results.push({
              id: fmt.id, product_id: product.id, product_name: product.name,
              name: fmt.name, package_type: fmt.package_type,
              cash_price: Number(fmt.cash_price), card_price: Number(fmt.card_price),
              oz_per_unit: Number(fmt.oz_per_unit), upc: fmt.upc,
            });
          }
        }
      }
      // Auto-add if exactly one result
      if (results.length === 1) {
        const item = results[0];
        const saleId = await ensureOpenSale();
        await api.post(`/sales/${saleId}/lines`, {
          product_id: item.product_id, sellable_format_id: item.id, qty: 1,
        });
        await refreshSale(saleId);
        setSearchResults([]);
        setSearchQuery("");
        return;
      }
      setSearchResults(results);
    } catch { /* search failures are silent */ }
  }, [ensureOpenSale, refreshSale]);

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => handleSearch(value), 300);
  };

  const addItemFromSearch = async (item: SearchResult) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setLoading(true);
    try {
      const saleId = await ensureOpenSale();
      await api.post(`/sales/${saleId}/lines`, {
        product_id: item.product_id, sellable_format_id: item.id, qty: 1,
      });
      await refreshSale(saleId);
      setSearchResults([]);
      setSearchQuery("");
      if (unknownUpc) {
        setUpcOffer({ upc: unknownUpc, formatId: item.id, label: `${item.product_name}, ${item.name}` });
        setUnknownUpc(null);
      }
    } catch (err) { console.error("Add failed:", err); }
    finally { setLoading(false); }
  };

  const updateLineQty = async (lineId: string, qty: number) => {
    if (!sale) return;
    if (qty <= 0) { await api.delete(`/sales/lines/${lineId}`); }
    else { await api.put(`/sales/lines/${lineId}/qty`, { qty }); }
    await refreshSale(sale.id);
  };

  const removeLine = async (lineId: string) => {
    if (!sale) return;
    await api.delete(`/sales/lines/${lineId}`);
    await refreshSale(sale.id);
  };

  const applyLineDiscount = async (lineId: string, type: "percent_off" | "dollar_off", value: number, reason: string, reasonType?: string) => {
    if (!sale) return;
    try {
      await api.post(`/sales/lines/${lineId}/discount`, {
        discount_type: type,
        discount_value: value,
        reason,
        discount_reason_type: reasonType,
      });
      await refreshSale(sale.id);
      showToast("Discount applied", "success");
    } catch (err) {
      console.error("Discount failed:", err);
      showToast("Failed to apply discount");
    }
  };

  const removeLineDiscount = async (lineId: string) => {
    if (!sale) return;
    try {
      await api.delete(`/sales/lines/${lineId}/discount`);
      await refreshSale(sale.id);
      showToast("Discount removed", "success");
    } catch (err) {
      console.error("Remove discount failed:", err);
    }
  };

  const applyCartDiscount = async (type: "percent_off" | "dollar_off", value: number, reason: string, reasonType?: string) => {
    if (!sale) return;
    try {
      await api.post(`/sales/${sale.id}/cart-discount`, {
        discount_type: type,
        discount_value: value,
        reason,
        discount_reason_type: reasonType,
      });
      await refreshSale(sale.id);
      setShowCartDiscount(false);
      showToast("Cart discount applied", "success");
    } catch (err) {
      console.error("Cart discount failed:", err);
      showToast("Failed to apply cart discount");
    }
  };

  const removeCartDiscount = async () => {
    if (!sale) return;
    try {
      await api.delete(`/sales/${sale.id}/cart-discount`);
      await refreshSale(sale.id);
      setShowCartDiscount(false);
      showToast("Cart discount removed", "success");
    } catch (err) {
      console.error("Remove cart discount failed:", err);
    }
  };

  const addByFormatId = async (productId: string, formatId: string) => {
    setLoading(true);
    try {
      const saleId = await ensureOpenSale();
      await api.post(`/sales/${saleId}/lines`, {
        product_id: productId, sellable_format_id: formatId, qty: 1,
      });
      await refreshSale(saleId);
    } catch { showToast("Failed to add item. Try again."); }
    finally { setLoading(false); }
  };

  const handleDrawerComplete = async (total: number) => {
    try {
      if (showDrawerCount === "open") {
        const res = await api.post("/sales/drawer/open", {
          register_id: "register-1",
          opening_balance: total,
        });
        setDrawerSession(res.data.id);
        setDrawerOpeningBalance(total);
        setDrawerCashSales(0);
        setDrawerCashRefunds(0);
      } else if (showDrawerCount === "close" && drawerSession) {
        await api.post(`/sales/drawer/${drawerSession}/close`, {
          closing_balance: total,
        });
        setDrawerSession(null);
        setDrawerOpeningBalance(0);
        setDrawerCashSales(0);
        setDrawerCashRefunds(0);
      }
    } catch (err) {
      console.error("Drawer operation failed:", err);
      const msg = err && typeof err === "object" && "response" in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      showToast(msg || "The drawer didn't save. Try again.");
      await loadDrawer();
    }
    setShowDrawerCount(null);
  };

  // NO SALE: open the drawer without ringing anything, for change and corrections.
  // Logged as a no-sale so an opened drawer is never unexplained at count time.
  const handleNoSale = async () => {
    const opened = await kickDrawer();
    setToastMsg(opened
      ? { text: "Drawer opened (no sale)", type: "success" }
      : { text: "No receipt printer found - open the drawer by hand", type: "error" });
    if (opened) {
      try { await api.post("/sales/drawer/no-sale", { drawer_session_id: drawerSession }); } catch { /* logging is best effort */ }
    }
  };

  const startCloseDrawer = async () => {
    await loadDrawer(); // today's cash sales and refunds, fresh from the server
    setShowDrawerCount("close");
  };

  const handlePaymentComplete = async () => {
    // Auto-earn loyalty points if a customer is attached
    if (loyaltyCustomer && sale) {
      try {
        const amount = Math.floor(Number(sale.total_cash || 0)); // 1 point per dollar
        if (amount > 0) {
          await api.post("/loyalty/earn", { customer_id: loyaltyCustomer.id, amount, sale_id: sale.id });
        }
      } catch (err) {
        console.error("Failed to earn loyalty points:", err);
      }
    }
    setShowPayment(false);
    setSale(null);
    setLoyaltyCustomer(null);
  };

  const voidSale = async () => {
    if (!sale) return;
    const reason = prompt("Reason for void:");
    if (!reason) return;
    await api.post(`/sales/${sale.id}/void`, { reason });
    setSale(null);
  };

  const toggleTrainingMode = () => {
    if (sale && sale.lines && sale.lines.length > 0) {
      showToast("Hold or complete the current sale before switching modes");
      return;
    }
    setSale(null);
    setTrainingMode((prev) => !prev);
  };

  const holdCurrentSale = async () => {
    if (!sale || !sale.lines || sale.lines.length === 0) return;
    try {
      await api.post(`/sales/${sale.id}/hold`, { label: holdLabel.trim() || "Held sale" });
      setSale(null);
      setShowHoldInput(false);
      setHoldLabel("");
      showToast("Sale held", "success");
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to hold sale";
      showToast(msg);
    }
  };

  const loadHeldSales = async () => {
    try {
      const res = await api.get("/sales/held/list");
      setHeldSales(res.data);
      setShowHeldSales(true);
    } catch {
      showToast("Failed to load held sales");
    }
  };

  const recallHeldSale = async (heldSaleId: string) => {
    if (sale && sale.lines && sale.lines.length > 0) {
      showToast("Hold or complete the current sale first");
      return;
    }
    try {
      const res = await api.post(`/sales/${heldSaleId}/recall`, { register_id: "register-1" });
      setSale(res.data);
      setShowHeldSales(false);
      showToast("Sale recalled", "success");
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to recall sale";
      showToast(msg);
    }
  };

  const lines = sale?.lines || [];
  const itemCount = lines.reduce((sum, l) => sum + l.qty, 0);
  const hasCartDiscount = sale && sale.cart_discount_type && sale.cart_discount_type !== "none" && Number(sale.cart_discount_amount) > 0;
  const totalDiscount = Number(sale?.total_discount || 0);

  return (
    <div className="h-screen flex flex-col" style={{ background: "var(--rbw-bg)" }}>
      {/* Top bar */}
      <div className="px-4 py-2 flex items-center justify-between shadow-md" style={{ background: "var(--rbw-navy)" }}>
        <div className="flex items-center gap-4">
          <img src="/images/logo-secondary.jpg" alt="RBW" className="h-7 rounded" />
          {isManager && (
            <div className="flex gap-0.5">
              {[
                { to: "/products", label: "Products" },
                { to: "/vendors", label: "Vendors" },
                { to: "/purchase-orders", label: "Orders" },
                { to: "/inventory", label: "Inventory" },
                { to: "/pricing", label: "Pricing" },
                { to: "/kegs", label: "Kegs" },
                { to: "/shelf-tags", label: "Tags" },
                { to: "/reports", label: "Reports" },
                { to: "/scheduling", label: "Schedule" },
                { to: "/payroll", label: "Payroll" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-2 py-1 text-xs rounded transition-all"
                  style={{ color: "var(--rbw-gray)" }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.color = "#fff";
                    (e.target as HTMLElement).style.background = "rgba(255,255,255,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.color = "var(--rbw-gray)";
                    (e.target as HTMLElement).style.background = "transparent";
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold px-3 py-1 rounded-lg" style={{ background: "rgba(255,255,255,0.1)", color: "var(--rbw-gold)" }}>
            {user?.first_name} {user?.last_name}
          </span>
          <span className="text-xs" style={{ color: "var(--rbw-gray)" }}>
            Register 1
          </span>
          <button
            onClick={() => setShowPinSwitch(true)}
            className="text-xs font-bold px-2 py-1 rounded-lg transition-all"
            style={{ background: "var(--rbw-blue)", color: "#fff" }}
          >
            SWITCH
          </button>
          <button onClick={logout} className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Training mode banner */}
      {trainingMode && (
        <div className="px-4 py-2 text-center font-bold text-sm tracking-widest"
          style={{ background: "#F59E0B", color: "#000" }}>
          TRAINING MODE — Nothing is saved. No inventory changes. No real charges.
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Cart area */}
        <div className="flex-1 flex flex-col" style={{ background: trainingMode ? "#FFFBEB" : "var(--rbw-bg-card)" }}>
          {/* Toast message */}
          {toastMsg && (
            <div className="px-3 py-2 text-sm font-medium flex justify-between items-center"
              style={{
                background: toastMsg.type === "error" ? "#fef2f2" : "#f0fdf4",
                color: toastMsg.type === "error" ? "#dc2626" : "#16a34a",
                borderBottom: `2px solid ${toastMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
              }}>
              <span>{toastMsg.text}</span>
              <button onClick={() => setToastMsg(null)} className="ml-2 font-bold">&times;</button>
            </div>
          )}

          {/* Scan/Search bar */}
          <div className="p-3" style={{ borderBottom: "2px solid var(--rbw-border)" }}>
            <div className="relative">
              <input
                ref={scanInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleScan(searchQuery); }}
                placeholder="Scan barcode or search product..."
                className="w-full px-4 py-3 text-lg rounded-xl outline-none transition-all"
                style={{
                  border: "2px solid var(--rbw-border)",
                  color: "var(--rbw-navy)",
                  background: "var(--rbw-bg)",
                }}
                onFocus={(e) => e.target.style.borderColor = "var(--rbw-blue)"}
                onBlur={(e) => e.target.style.borderColor = "var(--rbw-border)"}
                autoFocus
              />
              {loading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: "var(--rbw-blue)" }}>
                  Loading...
                </div>
              )}
            </div>

            {/* Search results dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-50 left-3 right-60 mt-1 rounded-xl shadow-2xl max-h-80 overflow-y-auto"
                style={{ background: "var(--rbw-bg-card)", border: "2px solid var(--rbw-blue)" }}>
                {searchResults.map((item) => (
                  <button
                    key={`${item.product_id}-${item.id}`}
                    onClick={() => addItemFromSearch(item)}
                    className="w-full text-left px-4 py-3 flex justify-between items-center transition-all"
                    style={{ borderBottom: "1px solid var(--rbw-border)" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = "var(--rbw-gray-light)"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = "transparent"}
                  >
                    <div>
                      <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{item.product_name}</div>
                      <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>{item.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>${item.cash_price.toFixed(2)}</div>
                      <div className="text-xs" style={{ color: "var(--rbw-blue-dark)" }}>card ${item.card_price.toFixed(2)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save the barcode that just found nothing onto the item picked by name */}
          {upcOffer && (
            <div className="mx-3 mt-3 rounded-xl p-3 flex flex-wrap items-center gap-3"
              style={{ background: "#eff6ff", border: "2px solid var(--rbw-blue)" }}>
              <div className="flex-1 min-w-0 text-sm" style={{ color: "var(--rbw-navy)" }}>
                <div className="font-bold">Save barcode {upcOffer.upc} to {upcOffer.label}?</div>
                <div style={{ color: "var(--rbw-gray-dark)" }}>Only if that barcode is on this exact pack. Then it scans next time.</div>
              </div>
              <button onClick={saveUpcOffer} disabled={savingUpc}
                className="px-4 py-2 rounded-xl font-bold text-white disabled:opacity-50"
                style={{ background: "var(--rbw-blue)" }}>{savingUpc ? "SAVING..." : "SAVE BARCODE"}</button>
              <button onClick={() => { setUpcOffer(null); scanInputRef.current?.focus(); }}
                className="px-4 py-2 rounded-xl font-bold"
                style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>NOT NOW</button>
            </div>
          )}

          {/* Quick buttons */}
          <QuickButtons onAddItem={addByFormatId} />

          {/* Cart items */}
          <CartDisplay
            lines={lines}
            onUpdateQty={updateLineQty}
            onRemove={removeLine}
            onLineDiscount={applyLineDiscount}
            onRemoveLineDiscount={removeLineDiscount}
            isManager={isManager}
          />

          {/* Upgrade suggestions */}
          {upgradeOffers.map((offer, i) => (
            <div key={i} className="px-4 py-3 flex items-center justify-between"
              style={{ background: "#DBEAFE", borderTop: "2px solid var(--rbw-blue)" }}>
              <div className="flex-1">
                <div className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                  {offer.current_lines.map(l => `${l.qty}x ${l.format_name}`).join(" + ")} = {offer.upgrade_qty}x {offer.upgrade_format_name}
                  {offer.leftover_lines.map(l => ` + ${l.qty}x ${l.format_name}`).join("")}
                </div>
                <div className="text-xs" style={{ color: "var(--rbw-blue-dark)" }}>
                  Save ${offer.savings.toFixed(2)} (${offer.current_total.toFixed(2)} → ${offer.upgrade_total.toFixed(2)})
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    await api.post(`/sales/${sale!.id}/upgrade`, {
                      product_id: offer.product_id,
                      upgrade_format_id: offer.upgrade_format_id,
                      upgrade_qty: offer.upgrade_qty,
                      leftover_formats: offer.leftover_lines,
                    });
                    await refreshSale(sale!.id);
                  } catch {
                    showToast("Failed to apply upgrade");
                  }
                }}
                className="ml-3 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
                style={{ background: "var(--rbw-blue)" }}
              >
                Upgrade
              </button>
              <button
                onClick={() => setUpgradeOffers(prev => prev.filter((_, j) => j !== i))}
                className="ml-2 text-xs px-2 py-1 rounded"
                style={{ color: "var(--rbw-muted)" }}
              >
                Dismiss
              </button>
            </div>
          ))}

          {/* Cart discount banner */}
          {hasCartDiscount && (
            <div className="px-4 py-2 flex items-center justify-between"
              style={{ background: "#FEF3C7", borderTop: "2px solid var(--rbw-gold)" }}>
              <div>
                <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                  Cart Discount: {sale!.cart_discount_type === "percent_off"
                    ? `${Number(sale!.cart_discount_value)}% off`
                    : `$${Number(sale!.cart_discount_value).toFixed(2)} off`}
                </span>
                <span className="text-sm ml-2" style={{ color: "var(--rbw-gray-dark)" }}>
                  (-${Number(sale!.cart_discount_amount).toFixed(2)})
                </span>
                {sale!.cart_discount_reason && (
                  <span className="text-xs ml-2" style={{ color: "var(--rbw-gray-dark)" }}>
                    &middot; {sale!.cart_discount_reason}
                  </span>
                )}
              </div>
              {isManager && (
                <button
                  onClick={() => setShowCartDiscount(true)}
                  className="text-xs font-bold px-2 py-1 rounded"
                  style={{ color: "var(--rbw-navy)" }}
                >
                  Edit
                </button>
              )}
            </div>
          )}

          {/* Discount summary */}
          {totalDiscount > 0 && (
            <div className="px-4 py-1 text-xs font-medium" style={{ color: "var(--rbw-danger)", borderTop: "1px solid var(--rbw-border)" }}>
              Total discounts: -${totalDiscount.toFixed(2)}
            </div>
          )}

          {/* Totals bar */}
          <div className="p-4" style={{ borderTop: "3px solid var(--rbw-navy)", background: "var(--rbw-bg)" }}>
            <div className="flex justify-between text-sm mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
              <span>{itemCount} item{itemCount !== 1 ? "s" : ""}</span>
              <span>Save ${(Number(sale?.total_card || 0) - Number(sale?.total_cash || 0)).toFixed(2)} with cash</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-3" style={{ background: "var(--rbw-navy)", color: "#fff" }}>
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--rbw-gold)" }}>
                  Cash / Debit
                </div>
                <div className="text-3xl font-bold mt-0.5">${Number(sale?.total_cash || 0).toFixed(2)}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--rbw-gray)" }}>
                  subtotal ${Number(sale?.subtotal_cash || 0).toFixed(2)} + tax ${Number(sale?.tax_cash || 0).toFixed(2)}
                </div>
              </div>
              <div className="rounded-xl p-3" style={{ border: "2px solid var(--rbw-border)" }}>
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--rbw-blue-dark)" }}>
                  Credit Card
                </div>
                <div className="text-3xl font-bold mt-0.5" style={{ color: "var(--rbw-navy)" }}>
                  ${Number(sale?.total_card || 0).toFixed(2)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "var(--rbw-gray-dark)" }}>
                  subtotal ${Number(sale?.subtotal_card || 0).toFixed(2)} + tax ${Number(sale?.tax_card || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Action panel */}
        <div className="w-44 p-3 flex flex-col gap-3" style={{ background: "var(--rbw-navy-dark)" }}>
          <button
            onClick={() => lines.length > 0 && setShowPayment(true)}
            disabled={lines.length === 0}
            className="py-8 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          >
            PAY
          </button>

          <button
            onClick={voidSale}
            disabled={!sale || lines.length === 0}
            className="py-4 rounded-xl font-bold transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: "var(--rbw-danger)", color: "#fff" }}
          >
            VOID
          </button>

          <button
            onClick={() => { if (lines.length > 0) setShowHoldInput(true); }}
            disabled={lines.length === 0}
            className="py-4 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-gray-light)" }}
          >
            HOLD
          </button>

          <button
            onClick={loadHeldSales}
            className="py-4 rounded-xl font-bold text-sm transition-all active:scale-95 relative"
            style={{ background: "var(--rbw-navy-light)", color: "#F59E0B" }}
          >
            HELD SALES
          </button>

          {isManager && (
            <button
              onClick={() => lines.length > 0 && setShowCartDiscount(true)}
              disabled={lines.length === 0}
              className="py-4 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                background: hasCartDiscount ? "#FEF3C7" : "var(--rbw-navy-light)",
                color: hasCartDiscount ? "var(--rbw-navy)" : "var(--rbw-gold)",
                border: hasCartDiscount ? "2px solid var(--rbw-gold)" : "2px solid transparent",
              }}
            >
              {hasCartDiscount
                ? `DISCOUNT -$${Number(sale!.cart_discount_amount).toFixed(2)}`
                : "DISCOUNT"}
            </button>
          )}

          <button
            onClick={() => setShowLoyalty(true)}
            className="py-4 rounded-xl font-bold text-sm transition-all active:scale-95 relative"
            style={{
              background: loyaltyCustomer ? "#2D8A4E20" : "var(--rbw-navy-light)",
              color: loyaltyCustomer ? "var(--rbw-success)" : "var(--rbw-blue)",
              border: loyaltyCustomer ? "2px solid var(--rbw-success)" : "2px solid transparent",
            }}
          >
            {loyaltyCustomer ? loyaltyCustomer.first_name : "LOYALTY"}
            {loyaltyCustomer && (
              <div className="text-[10px] font-normal mt-0.5">
                {loyaltyCustomer.points_balance} pts
              </div>
            )}
          </button>

          <div className="flex-1" />

          {!drawerSession ? (
            <button
              onClick={() => setShowDrawerCount("open")}
              className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
              style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-success)" }}
            >
              OPEN DRAWER
            </button>
          ) : (
            <button
              onClick={startCloseDrawer}
              className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
              style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-gold)" }}
            >
              CLOSE DRAWER
            </button>
          )}

          <button
            onClick={handleNoSale}
            className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
            style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-gray-light)" }}
          >
            NO SALE
          </button>

          <button
            onClick={() => setShowClockPunch(true)}
            className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          >
            TIME CLOCK
          </button>

          <Link
            to="/returns"
            className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
            style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-gray)" }}
          >
            RETURN
          </Link>

          {isManager && (
            <Link
              to="/reports"
              className="py-3 rounded-xl font-bold text-center text-sm transition-all active:scale-95"
              style={{ background: "var(--rbw-navy-light)", color: "var(--rbw-gold)" }}
            >
              MANAGER
            </Link>
          )}

          <button
            onClick={toggleTrainingMode}
            className="py-3 rounded-xl font-bold text-center text-xs transition-all active:scale-95"
            style={{
              background: trainingMode ? "#F59E0B" : "var(--rbw-navy-light)",
              color: trainingMode ? "#000" : "var(--rbw-gray-dark)",
              border: trainingMode ? "2px solid #D97706" : "2px solid transparent",
            }}
          >
            {trainingMode ? "EXIT TRAINING" : "TRAINING"}
          </button>
        </div>
      </div>

      {showPayment && sale && (
        <PaymentModal sale={sale} onComplete={handlePaymentComplete} onClose={() => setShowPayment(false)} trainingMode={trainingMode} />
      )}

      {showDrawerCount && (
        <DrawerCount
          mode={showDrawerCount}
          sessionId={drawerSession}
          openingBalance={drawerOpeningBalance}
          cashSalesTotal={drawerCashSales}
          cashRefundsTotal={drawerCashRefunds}
          onComplete={handleDrawerComplete}
          onCancel={() => setShowDrawerCount(null)}
        />
      )}

      {showLoyalty && (
        <LoyaltyLookup
          currentCustomer={loyaltyCustomer}
          onCustomerSelected={(c) => { setLoyaltyCustomer(c); setShowLoyalty(false); }}
          onRemoveCustomer={() => { setLoyaltyCustomer(null); setShowLoyalty(false); }}
          onClose={() => setShowLoyalty(false)}
          onRedeemPoints={handleRedeemPoints}
          redemptionThreshold={loyaltySettings.redemption_threshold}
          redemptionValue={loyaltySettings.redemption_value}
        />
      )}

      {formatChoices && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setFormatChoices(null)}>
          <div className="rounded-2xl shadow-2xl w-full max-w-md p-6"
            style={{ background: "var(--rbw-bg-card)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              {new Set(formatChoices.map((f) => f.product_id)).size > 1 ? "Which product?" : "Which size?"}
            </h3>
            <p className="text-sm mb-4" style={{ color: "var(--rbw-muted)" }}>
              {new Set(formatChoices.map((f) => f.product_id)).size > 1
                ? "This barcode is on more than one product."
                : formatChoices[0]?.product_name}
            </p>
            <div className="space-y-2">
              {formatChoices.map((f) => (
                <button
                  key={f.id}
                  onClick={async () => {
                    setFormatChoices(null);
                    setLoading(true);
                    try {
                      await addFormatToSale({ product_id: f.product_id, sellable_format_id: f.id });
                    } catch {
                      showToast("Failed to add item");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="w-full text-left px-4 py-3 rounded-xl flex items-center justify-between transition-all hover:scale-[1.01]"
                  style={{ background: "var(--rbw-bg)", border: "2px solid var(--rbw-border)" }}
                >
                  <div>
                    {new Set(formatChoices.map((x) => x.product_id)).size > 1 && (
                      <div className="text-xs font-medium" style={{ color: "var(--rbw-muted)" }}>{f.product_name}</div>
                    )}
                    <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>{f.name}</div>
                    <div className="text-xs" style={{ color: "var(--rbw-muted)" }}>{f.package_type} &middot; {f.oz_per_unit} oz</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm" style={{ color: "var(--rbw-gold-dark)" }}>${f.cash_price.toFixed(2)}</div>
                    <div className="text-xs" style={{ color: "var(--rbw-blue-dark)" }}>${f.card_price.toFixed(2)} card</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setFormatChoices(null)}
              className="mt-4 w-full text-center text-sm py-2"
              style={{ color: "var(--rbw-muted)" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showCartDiscount && sale && (
        <DiscountModal
          title="Cart Discount"
          subtitle={`Apply discount to entire sale (${itemCount} items)`}
          onConfirm={applyCartDiscount}
          onClose={() => setShowCartDiscount(false)}
          onRemove={removeCartDiscount}
          hasExistingDiscount={!!hasCartDiscount}
        />
      )}

      {/* Hold sale input */}
      {showHoldInput && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowHoldInput(false)}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6"
            style={{ background: "var(--rbw-bg-card)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              Hold Sale
            </h3>
            <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
              Park this sale and start a new one. Add a label so you can find it later.
            </p>
            <input
              type="text"
              value={holdLabel}
              onChange={(e) => setHoldLabel(e.target.value)}
              placeholder="e.g. Mike - forgot wallet, Johnson wedding Friday"
              className="w-full px-3 py-2.5 rounded-lg mb-4 outline-none text-sm"
              style={{
                border: "2px solid var(--rbw-border)",
                color: "var(--rbw-navy)",
                background: "var(--rbw-bg)",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
              onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              onKeyDown={(e) => { if (e.key === "Enter") holdCurrentSale(); }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowHoldInput(false); setHoldLabel(""); }}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
                style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}
              >
                Cancel
              </button>
              <button
                onClick={holdCurrentSale}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
                style={{ background: "var(--rbw-navy)", color: "var(--rbw-gold)" }}
              >
                Hold Sale
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Register time clock */}
      {showClockPunch && <ClockPunch onClose={() => setShowClockPunch(false)} />}

      {/* PIN quick-switch */}
      {showPinSwitch && (
        <PinSwitch
          currentUserName={`${user?.first_name || ""} ${user?.last_name || ""}`}
          onPinSubmit={handlePinSwitch}
          onCancel={() => setShowPinSwitch(false)}
        />
      )}

      {/* Held sales list */}
      {showHeldSales && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowHeldSales(false)}>
          <div className="rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto"
            style={{ background: "var(--rbw-bg-card)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
                Held Sales
              </h3>
              <button onClick={() => setShowHeldSales(false)} className="text-xl"
                style={{ color: "var(--rbw-gray)" }}>&times;</button>
            </div>

            {heldSales.length === 0 ? (
              <div className="text-center py-8" style={{ color: "var(--rbw-gray)" }}>
                <div className="text-3xl mb-2">0</div>
                <div className="text-sm">No held sales</div>
              </div>
            ) : (
              <div className="space-y-2">
                {heldSales.map((held) => (
                  <button
                    key={held.id}
                    onClick={() => recallHeldSale(held.id)}
                    className="w-full text-left rounded-xl p-4 transition-all active:scale-[0.98]"
                    style={{ background: "var(--rbw-bg)", border: "2px solid var(--rbw-border)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--rbw-gold)")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--rbw-border)")}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-sm" style={{ color: "var(--rbw-navy)" }}>
                          {held.hold_label}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--rbw-gray-dark)" }}>
                          {Number(held.item_count)} items &middot; held by {held.held_by_name?.split(" ")[0] || "?"} &middot; {new Date(held.held_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>
                          ${Number(held.total_cash).toFixed(2)}
                        </div>
                        <div className="text-xs" style={{ color: "var(--rbw-blue-dark)" }}>
                          card ${Number(held.total_card).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs mt-2 font-medium" style={{ color: "var(--rbw-gold-dark)" }}>
                      Tap to recall to register
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
